import { useCallback, useMemo, useState } from 'react';
import type { WorkshopManifest, WorkshopModule, WorkshopTopic } from '@/lib/api/types';

export interface Position {
  module: number;
  topic: number;
}

export interface WorkshopNavigation {
  position: Position;
  module: WorkshopModule | null;
  topic: WorkshopTopic | null;
  /** 0-1, across every topic in the workshop. */
  progress: number;
  isFirst: boolean;
  isLast: boolean;
  goTo: (position: Position) => void;
  goToModule: (module: number) => void;
  next: () => void;
  previous: () => void;
}

function clamp(value: number, max: number): number {
  return Math.max(0, Math.min(value, max));
}

/**
 * Position state for a workshop: which module and topic are open, and how to
 * move between them. Kept free of rendering and of transport so both the
 * self-paced viewer and presenter-led follow mode (US-4.3) drive the same
 * model — follow mode just calls `goTo` with the presenter's position.
 */
export function useWorkshopNavigation(manifest: WorkshopManifest | null): WorkshopNavigation {
  const modules = useMemo(() => manifest?.modules ?? [], [manifest]);
  const [position, setPosition] = useState<Position>({ module: 0, topic: 0 });

  const module = modules[clamp(position.module, modules.length - 1)] ?? null;
  const topics = module?.topics ?? [];
  const topic = topics[clamp(position.topic, topics.length - 1)] ?? null;

  const totals = useMemo(() => {
    const perModule = modules.map((entry) => entry.topics.length);
    const total = perModule.reduce((sum, count) => sum + count, 0);
    const before = perModule
      .slice(0, position.module)
      .reduce((sum: number, count: number) => sum + count, 0);
    return { total, completed: before + position.topic };
  }, [modules, position]);

  const goTo = useCallback(
    (next: Position) => {
      const moduleIndex = clamp(next.module, modules.length - 1);
      const topicCount = modules[moduleIndex]?.topics.length ?? 1;
      setPosition({ module: moduleIndex, topic: clamp(next.topic, topicCount - 1) });
    },
    [modules],
  );

  const goToModule = useCallback((moduleIndex: number) => goTo({ module: moduleIndex, topic: 0 }), [goTo]);

  const isFirst = position.module === 0 && position.topic === 0;
  const isLast = position.module === modules.length - 1 && position.topic === topics.length - 1;

  const next = useCallback(() => {
    setPosition((current) => {
      const currentTopics = modules[current.module]?.topics.length ?? 0;
      if (current.topic < currentTopics - 1) return { ...current, topic: current.topic + 1 };
      if (current.module < modules.length - 1) return { module: current.module + 1, topic: 0 };
      return current;
    });
  }, [modules]);

  const previous = useCallback(() => {
    setPosition((current) => {
      if (current.topic > 0) return { ...current, topic: current.topic - 1 };
      if (current.module > 0) {
        const previousModule = current.module - 1;
        return { module: previousModule, topic: (modules[previousModule]?.topics.length ?? 1) - 1 };
      }
      return current;
    });
  }, [modules]);

  return {
    position,
    module,
    topic,
    progress: totals.total === 0 ? 0 : totals.completed / totals.total,
    isFirst,
    isLast,
    goTo,
    goToModule,
    next,
    previous,
  };
}
